<template>
  <div
    class="rate-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="rate-modal-title"
  >

    <!-- ─── Close ─────────────────────────────────────────────── -->
    <button
      class="rate-modal__close"
      @click="emit('cancel')"
      :disabled="pending"
      aria-label="Close rating dialog"
    >
      <ion-icon :icon="closeOutline" />
    </button>

    <!-- ─── Hero ──────────────────────────────────────────────── -->
    <div class="rate-modal__hero">
      <div class="rate-modal__hero-icon" aria-hidden="true">
        <ion-icon :icon="star" />
      </div>
      <h2 id="rate-modal-title" class="rate-modal__hero-title">Rate Syntry</h2>
      <p class="rate-modal__hero-sub">
        Enjoying the app? Let us know how we're doing.
      </p>
    </div>

    <!-- ─── Star rating ───────────────────────────────────────── -->
    <div class="rate-modal__stars" role="radiogroup" aria-label="Star rating">
      <button
        v-for="n in 5"
        :key="n"
        type="button"
        class="rate-modal__star"
        :class="{ 'rate-modal__star--filled': n <= form.rating }"
        role="radio"
        :aria-checked="n === form.rating"
        :aria-label="`${n} star${n > 1 ? 's' : ''}`"
        :disabled="pending"
        @click="form.rating = n"
      >
        <ion-icon :icon="n <= form.rating ? star : starOutline" />
      </button>
    </div>
    <p class="rate-modal__rating-label" aria-live="polite">{{ ratingLabel }}</p>

    <!-- ─── Review ────────────────────────────────────────────── -->
    <div class="rate-modal__field">
      <label class="rate-modal__label" for="rate-review">
        Your review <span class="rate-modal__label-optional">(Optional)</span>
      </label>
      <textarea
        id="rate-review"
        class="rate-modal__textarea"
        placeholder="Tell us what you love or what we can improve…"
        v-model="form.review"
        rows="5"
        :disabled="pending"
        aria-label="Your review"
      />
    </div>

    <!-- ─── Submit ────────────────────────────────────────────── -->
    <button
      class="rate-modal__submit"
      @click="onSubmit"
      :disabled="form.rating === 0 || pending"
    >
      <ion-spinner v-if="pending" name="crescent" class="rate-modal__spinner" />
      <template v-else>
        Submit Review
        <ion-icon :icon="arrowForwardOutline" aria-hidden="true" />
      </template>
    </button>

  </div>
</template>

<script setup lang="ts">
import { reactive, computed } from 'vue';
import { IonIcon, IonSpinner } from '@ionic/vue';
import { closeOutline, star, starOutline, arrowForwardOutline } from 'ionicons/icons';

export interface RatingPayload {
  rating: number;
  review: string;
}

const props = withDefaults(
  defineProps<{
    /** When true, the parent is submitting — controls lock and the button shows a spinner. */
    pending?: boolean;
  }>(),
  { pending: false },
);

const emit = defineEmits<{
  (e: 'confirm', payload: RatingPayload): void;
  (e: 'cancel'): void;
}>();

const form = reactive<RatingPayload>({
  rating: 0,
  review: '',
});

const RATING_LABELS = ['Tap a star to rate', 'Terrible', 'Poor', 'Okay', 'Good', 'Excellent'];
const ratingLabel = computed(() => RATING_LABELS[form.rating] ?? RATING_LABELS[0]);

function onSubmit(): void {
  if (form.rating === 0 || props.pending) return;
  emit('confirm', { ...form });
}
</script>

<style scoped lang="scss">
.rate-modal {
  position: relative;
  /* Frosted-glass surface — translucent over a blurred backdrop. */
  background: rgba(var(--syntry-surface-rgb), 0.78);
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  border: 1px solid rgba(var(--syntry-surface-rgb), 0.55);
  border-radius: var(--syntry-radius-modal);
  box-shadow: var(--syntry-shadow-elevated);
  padding: var(--sp-7) var(--sp-6) var(--sp-6);
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  max-height: 90vh;
  overflow-y: auto;

  /* ─── Close ─── */
  &__close {
    position: absolute;
    top: var(--sp-4);
    right: var(--sp-4);
    width: 36px;
    height: 36px;
    min-width: 36px;
    background: rgba(var(--syntry-surface-rgb), 0.6);
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 20px;
    color: var(--syntry-text-muted);
    padding: 0;
    -webkit-tap-highlight-color: transparent;

    &:active { opacity: 0.7; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }

  /* ─── Hero ─── */
  &__hero {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: var(--sp-2);
  }

  &__hero-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: var(--syntry-orange-soft);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 30px;
    color: var(--syntry-orange);
    margin-bottom: var(--sp-1);
  }

  &__hero-title {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    color: var(--syntry-text-strong);
    letter-spacing: -0.01em;
  }

  &__hero-sub {
    margin: 0;
    font-size: var(--syntry-fs-small);
    color: var(--syntry-text-muted);
    line-height: 1.5;
    max-width: 32ch;
  }

  /* ─── Stars ─── */
  &__stars {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-2);
    margin-top: var(--sp-1);
  }

  &__star {
    background: none;
    border: none;
    padding: var(--sp-1);
    cursor: pointer;
    font-size: 38px;
    color: var(--syntry-border);
    display: flex;
    align-items: center;
    justify-content: center;
    -webkit-tap-highlight-color: transparent;
    transition: transform 120ms ease, color 120ms ease;

    &--filled { color: var(--syntry-orange); }
    &:active { transform: scale(0.9); }
    &:disabled { cursor: not-allowed; }
  }

  &__rating-label {
    text-align: center;
    font-size: var(--syntry-fs-small);
    font-weight: 600;
    color: var(--syntry-text-muted);
    margin: calc(-1 * var(--sp-2)) 0 0;
    min-height: 18px;
  }

  /* ─── Review ─── */
  &__field {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
  }

  &__label {
    font-size: var(--syntry-fs-small);
    font-weight: 600;
    color: var(--syntry-text-strong);
    letter-spacing: 0.01em;
  }

  &__label-optional {
    font-weight: 400;
    color: var(--syntry-text-soft);
  }

  &__textarea {
    width: 100%;
    border: 1px solid var(--syntry-border);
    border-radius: var(--syntry-radius-md);
    padding: var(--sp-3) var(--sp-4);
    font-size: var(--syntry-fs-body);
    font-family: var(--syntry-font);
    color: var(--syntry-text);
    background: rgba(var(--syntry-surface-rgb), 0.5);
    outline: none;
    resize: none;
    box-sizing: border-box;
    min-height: 110px;
    transition: border-color 150ms ease, box-shadow 150ms ease;

    &::placeholder { color: var(--syntry-text-soft); }
    &:focus {
      border-color: var(--syntry-primary);
      box-shadow: 0 0 0 3px rgba(var(--ion-color-primary-rgb), 0.15);
    }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }

  /* ─── Submit ─── */
  &__submit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-2);
    height: 52px;
    border: none;
    border-radius: var(--syntry-radius-button);
    background: var(--syntry-gradient-primary);
    color: #ffffff;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    font-family: var(--syntry-font);
    box-shadow: var(--syntry-shadow-button);
    transition: opacity 150ms ease;

    ion-icon { font-size: 18px; }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
      box-shadow: none;
    }
    &:not(:disabled):active { opacity: 0.88; }
  }

  &__spinner {
    width: 22px;
    height: 22px;
    --color: #ffffff;
  }
}
</style>
